import { useState } from "react";
import { Search, X, FileDown } from "lucide-react";
import { CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { searchStyles, outlineChromeButton } from "@/lib/status-styles";
import { Button } from "@/components/ui/button.jsx";
import { LoadingSpinner } from "@/components/ui/loading";

/**
 * Reusable card header component with optional actions and optional search functionality
 * @param {Object} props - Component props
 * @param {string} props.title - Header title
 * @param {Array} [props.actions] - Array of actions to display. Each action is either a button
 *   ({ id, text, executeAction, actionType }) or a custom element ({ id, element: ReactNode }).
 * @param {boolean} [props.searchEnabled] - Display search field
 * @param {string} [props.searchValue] - Current search value
 * @param {Function} [props.onSearchChange] - Search change handler
 * @param {string} [props.searchPlaceholder] - Search input placeholder
 * @param {string} [props.searchTestId] - Test ID for search input
 * @param {string} [props.titleTestId] - Test ID for title
 * @returns {JSX.Element} Card header with search
 */
export function CardHeaderActions({
  title,
  actions = [],
  searchEnabled = false,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  searchTestId = "input-search",
  titleTestId = "card-title",
}) {
  const [isActionExecuting, setIsActionExecuting] = useState(false);

  /**
   *  Render Icon for Action Button
   **/
  const renderIcon = (type) => {
    switch (type) {
      case "Download":
        return <FileDown className='h-4 w-4 mr-1' />;
      case "Search":
        return <Search className={searchStyles.icon} />;
      case "Clear":
        return <X className='h-4 w-4' />;
      default:
        return null;
    }
  };

  return (
    <CardHeader className='shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-card px-6 py-4 rounded-t-lg border-b border-border'>
      <CardTitle
        className='text-lg font-semibold text-foreground'
        data-testid={titleTestId}>
        {title}
      </CardTitle>
      <div className='flex flex-row items-center align-middle gap-4'>
        {/* Actions */}
        {isActionExecuting ? (
          <LoadingSpinner />
        ) : actions.length > 0 ? (
          <div className='flex gap-4 min-h-9'>
            {actions.map((action) => {
              if (action.element) {
                return <div key={action.id}>{action.element}</div>;
              }
              return (
                <Button
                  key={action.id}
                  variant='outline'
                  size='sm'
                  onClick={() => {
                    setIsActionExecuting(true);
                    action.executeAction();
                    setIsActionExecuting(false);
                  }}
                  className={outlineChromeButton}>
                  {renderIcon(action.actionType)}
                  {action.text}
                </Button>
              );
            })}
          </div>
        ) : (
          ""
        )}

        {/* Search */}
        {searchEnabled ? (
          <div className='flex items-center space-x-3'>
            <div className={searchStyles.container}>
              <Input
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                className={searchStyles.input}
                data-testid={searchTestId}
              />
              {searchValue ? (
                <button
                  onClick={() => onSearchChange("")}
                  className='absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground cursor-pointer hover:text-foreground transition-colors z-10'
                  data-testid='button-clear-search'
                  title='Clear search'>
                  {renderIcon("Clear")}
                </button>
              ) : (
                renderIcon("Search")
              )}
            </div>
          </div>
        ) : (
          ""
        )}
      </div>
    </CardHeader>
  );
}
